import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, provide, watch } from 'vue'

//echarts
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'

import {
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  DataZoomComponent,
} from 'echarts/components'

import VChart, { THEME_KEY } from 'vue-echarts'

interface TemperatureReading {
  timestamp: string
  temperature: number
  minTemperature?: number
  maxTemperature?: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TemperatureChart',
  props: {
  temperatureData: {
    type: Array as () => TemperatureReading[],
    required: true,
  },
  showMinMax: {
    type: Boolean,
    default: false
  }
},
  setup(__props) {

const props = __props

// echarts
use([
  LineChart,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  DataZoomComponent,
  CanvasRenderer,
])

provide(THEME_KEY, 'light')

const option = ref({
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross'
    },
    formatter: (params: Array<{axisValue: string, color: string, seriesName: string, value: number}>) => {
      const timestamp = params[0].axisValue
      let tooltipContent = `<div style="padding: 8px;"><strong>${timestamp}</strong><br/>`
      
      params.forEach((param) => {
        const color = param.color
        const seriesName = param.seriesName
        const value = param.value
        tooltipContent += `<span style="color: ${color};">●</span> ${seriesName}: ${value}°C<br/>`
      })
      
      tooltipContent += '</div>'
      return tooltipContent
    }
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '15%',
    top: '8%',
    containLabel: true
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: [] as string[],
    axisLabel: {
      rotate: 45,
      fontSize: 10,
      formatter: (value: string) => {
        const date = new Date(value)
        return date.toLocaleDateString('es-ES', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    }
  },
  yAxis: {
    type: 'value',
    name: 'Temperatura (°C)',
    axisLabel: {
      formatter: '{value}°C',
      color: '#FF6B35'
    },
    axisLine: {
      lineStyle: {
        color: '#FF6B35'
      }
    },
    min: function(value: {min: number, max: number}) {
      return Math.floor(value.min - 2)
    },
    max: function(value: {min: number, max: number}) {
      return Math.ceil(value.max + 2)
    },
    splitLine: {
      lineStyle: {
        color: '#f0f0f0',
        type: 'dashed'
      }
    }
  },
  dataZoom: [
    {
      type: 'slider',
      show: true,
      xAxisIndex: [0],
      start: 0,
      end: 100,
      height: 20,
      bottom: 5
    }
  ],
  series: [
    {
      name: 'Temperatura',
      type: 'line',
      data: [] as number[],
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      lineStyle: {
        color: '#FF6B35',
        width: 4
      },
      itemStyle: {
        color: '#FF6B35',
        borderWidth: 2,
        borderColor: '#fff'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(255, 107, 53, 0.4)' },
            { offset: 0.5, color: 'rgba(255, 107, 53, 0.2)' },
            { offset: 1, color: 'rgba(255, 107, 53, 0.05)' }
          ]
        }
      },
      markPoint: {
        data: [
          { type: 'max', name: 'Máximo' },
          { type: 'min', name: 'Mínimo' }
        ],
        itemStyle: {
          color: '#FF6B35'
        }
      },
      markLine: {
        data: [
          { type: 'average', name: 'Promedio' }
        ],
        lineStyle: {
          color: '#FF6B35',
          type: 'dashed'
        }
      }
    }
  ]
})

const chartRef = ref<InstanceType<typeof VChart> | null>(null)

const updateChartData = (data: TemperatureReading[]) => {
  const timestamps = data.map(item => item.timestamp)
  const temperatureData = data.map(item => item.temperature)

  option.value.xAxis.data = timestamps
  option.value.series[0].data = temperatureData

  // Si hay datos de min/max, agregar series adicionales
  if (props.showMinMax && data.some(item => item.minTemperature !== undefined || item.maxTemperature !== undefined)) {
    const minData = data.map(item => item.minTemperature ?? 0)
    const maxData = data.map(item => item.maxTemperature ?? 0)

    // Agregar series para min y max si no existen
    if (option.value.series.length === 1) {
      const minSeries = {
        name: 'Temperatura Mínima',
        type: 'line',
        data: minData,
        smooth: true,
        symbol: 'triangle',
        symbolSize: 6,
        lineStyle: {
          color: '#42A5F5',
          width: 2
        },
        itemStyle: {
          color: '#42A5F5',
          borderWidth: 1,
          borderColor: '#fff'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(66, 165, 245, 0.1)' },
              { offset: 0.5, color: 'rgba(66, 165, 245, 0.05)' },
              { offset: 1, color: 'rgba(66, 165, 245, 0.02)' }
            ]
          }
        },
        markPoint: {
          data: [
            { type: 'min', name: 'Mínimo' }
          ],
          itemStyle: {
            color: '#42A5F5'
          }
        },
        markLine: {
          data: [],
          lineStyle: {
            color: '#42A5F5',
            type: 'dashed'
          }
        }
      }
      
      const maxSeries = {
        name: 'Temperatura Máxima',
        type: 'line',
        data: maxData,
        smooth: true,
        symbol: 'triangle',
        symbolSize: 6,
        lineStyle: {
          color: '#EF5350',
          width: 2
        },
        itemStyle: {
          color: '#EF5350',
          borderWidth: 1,
          borderColor: '#fff'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(239, 83, 80, 0.1)' },
              { offset: 0.5, color: 'rgba(239, 83, 80, 0.05)' },
              { offset: 1, color: 'rgba(239, 83, 80, 0.02)' }
            ]
          }
        },
        markPoint: {
          data: [
            { type: 'max', name: 'Máximo' }
          ],
          itemStyle: {
            color: '#EF5350'
          }
        },
        markLine: {
          data: [],
          lineStyle: {
            color: '#EF5350',
            type: 'dashed'
          }
        }
      }
      
      option.value.series.push(minSeries, maxSeries)
    } else {
      // Actualizar datos existentes
      const series1 = option.value.series[1] as typeof option.value.series[0]
      const series2 = option.value.series[2] as typeof option.value.series[0]
      series1.data = minData
      series2.data = maxData
    }
  }
}

watch(
  () => props.temperatureData,
  (newData) => {
    updateChartData(newData)
  },
  { immediate: true, deep: true }
)

watch(
  () => props.showMinMax,
  () => {
    updateChartData(props.temperatureData)
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(VChart), {
    ref_key: "chartRef",
    ref: chartRef,
    class: "chart",
    option: option.value,
    style: {"height":"300px","width":"100%"}
  }, null, 8, ["option"]))
}
}

})